import { useEffect, useState } from 'react';
import redux, { IState } from '../../redux/redux';
import Icon, { IconType } from '../Icon/Icon';
import UserLogin from '../UserLogin/UserLogin';
import Button from '../_Core/Button';
import { IBranchTask } from '../../redux/types';
import Modal from '../Modal/Modal';
import BranchPortal from '../Setup/Branches/BranchPortal';
import StoreX, { store } from '../../redux/oldStore';
import { ServerResponse } from '../../utils/Server';
import { sanitizeHtml } from '../../utils/Tools';
import { toast } from 'react-toastify';
import { FormatDate } from '../../utils/Tools';

interface IProps {
  state: IState;
  type: string;
}

interface IWidgetData {}

interface IImportantDateResponse {
  data: IImportantDate[];
}

interface IImportantDate {
  PublicId?: string;
  Order?: number;
  Date?: string;
  EndDate?: string;
  DateHasTime?: boolean;
  EndDateHasTime?: boolean;
  IncludeInScheduleWidget?: boolean;
  Name?: string;
  SubLabel?: string;
  Group?: string;
  IsRange?: boolean;
  RangeDays?: number;
  IsActive?: boolean;
}
interface IImportantLinkResponse {
  data: IImportantLink[];
}

interface IImportantLink {
  ExternalLink?: string;
  FlexLink?: string;
  PageLink?: string;
  Name?: string;
  OpenInNewTab?: boolean;
  Order?: number;
  PublicId?: string;
  SystemLink?: string;
  Icon?: string;
}

interface IImportantLocationsResponse {
  data: IImportantLocations[];
}

interface IImportantLocations {
  Name?: string;
  Order?: number;
  LocationDetails?: string;
  LinkToMap?: string;
  AddressLine1?: string;
  AddressLine2?: string;
  AddressLine3?: string;
  AddressLine4?: string;
}

const Widget = (props: IProps) => {
  const [data, setData] = useState<IWidgetData>();
  const [profileImageFailedToLoad, setProfileImageFailedToLoad] = useState<boolean>();
  const [selectedBranch, setSelectedBranch] = useState<IBranchTask>();
  const [showBranch, setShowBranch] = useState<boolean>(false);

  const [dateWidgetData, setDateWidgetData] = useState<IImportantDateResponse>();
  const [linkWidgetData, setLinkWidgetData] = useState<IImportantLinkResponse>();
  const [locationWidgetData, setLocationsWidgetData] = useState<IImportantLocationsResponse>();
  const [dateGroups, setDateGroups] = useState<string[]>([]);

  useEffect(() => {
    init();
  }, []);

  const logout = () => {
    redux.NotReady();
    window.location.href = StoreX.BuildUrlWithFair('../app/logout');
  };

  const init = () => {
    switch (props.type.toUpperCase()) {
      case 'LINKS':
        store.server.postApi<ServerResponse<IImportantLinkResponse>>(`../Important/Data`, { type: 'link' }).then((x) => {
          if (x.Success) {
            setLinkWidgetData(x.Value);
          } else {
            toast.error(`${x.Message}`, { autoClose: false });
          }
        });
        break;
      case 'LOCATION':
        store.server.postApi<ServerResponse<IImportantLocationsResponse>>(`../Important/Data`, { type: 'location' }).then((x) => {
          if (x.Success) {
            setLocationsWidgetData(x.Value);
          } else {
            toast.error(`${x.Message}`, { autoClose: false });
          }
        });
        break;
      case 'DATE':
      case 'SCHEDULE':
        store.server.postApi<ServerResponse<IImportantDateResponse>>(`../Important/Data`, { type: 'date' }).then((x) => {
          if (x.Success) {
            setDateWidgetData(x.Value);
            let groupDic = new Set<string>();
            let groups: string[] = [];
            x.Value.data.forEach((x) => {
              let g = x.Group || '';
              if (!groupDic.has(g)) {
                groupDic.add(g);
                groups.push(g);
              }
            });
            setDateGroups(groups);
          } else {
            toast.error(`${x.Message}`, { autoClose: false });
          }
        });
        break;
    }
  };

  const buildWidget = () => {
    switch (props.type.toUpperCase()) {
      case 'ACCOUNT':
        return buildAccountWidget();
      case 'BRANCH':
        if (props.state.branchInfo) return buildBranchWidget(props.state.branchInfo);
        return <></>;
      case 'BRANCH-SUB':
        if (props.state.branchInfo?.SubBranch) return buildBranchWidget(props.state.branchInfo.SubBranch);
        return <></>;
      case 'SCHEDULE':
        return buildScheduleWidget();
        break;
      case 'LINKS':
        return buildLinksWidget();
        break;
      case 'LOCATION':
        return buildLocationWidget();
        break;
      default:
        return (
          <div className="widget text-center">
            <h3>Unknown Widget "{props.type}"</h3>
          </div>
        );
    }
  };

  const buildAccountWidget = () => {
    if (props.state.User?.signedIn) {
      let user = props.state.User;
      return (
        <div className="widget widget-account">
          <div className="widget-header">
            <div className="widget-profile-img">
              {user.ProfileLink && !profileImageFailedToLoad ? (
                <>
                  <img
                    src={user.ProfileLink}
                    alt="Profile Image"
                    onError={(x) => {
                      setProfileImageFailedToLoad(true);
                    }}
                  />
                </>
              ) : (
                <>
                  <Icon type={IconType.userProfileImage} />
                </>
              )}
            </div>
            <div className="widget-user-info">
              <h4>
                {user.firstName} {user.lastName}
              </h4>
              <div className="small">{user.email}</div>
            </div>
            <div className="bottom-right">
              <Button type="logout" text={label('WidgetAccountLogout', 'Logout')} extraSmall={true} onClick={logout} />
            </div>
          </div>
        </div>
      );
    } else {
      //Create Account / Sign in
      return (
        <div className="widget">
          <h3
            dangerouslySetInnerHTML={{
              __html: headingHtml('WidgetAccount', '<i class="fa fa-user text-muted"></i>&nbsp;Account Info', 'fa fa-user text-muted'),
            }}
          />
          <Button
            type="login"
            addClass="btn-block"
            text={label('WidgetAccountLogin', 'Login')}
            onClick={() => {
              redux.showLogin(true);
            }}
          />
          {props.state.Settings.ShowRegistrationOnHomePage && <>
          <a className='btn btn-block' href={StoreX.BuildUrlWithFair('/App/User/New')} ><Icon type={IconType.userPlus}/> {label('WidgetAccountRegister', 'Register')}</a>
          </>}
          {/* <Button type="register" addClass="btn-block" text="Register" /> */}
        </div>
      );
    }
  };

  const buildBranchWidget = (info: IBranchTask) => {
    return (
      <div
        className={`widget branch-entry-bar ${info.ClassName ?? ''} ${info.CallToActionText ? '' : 'click'}`}
        onClick={() => {
          if (!info.CallToActionText) {
            setSelectedBranch(info);
            setShowBranch(true);
          }
        }}>
        <div className="entry-bar-text">
          <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(info.Title) }}></div>
        </div>
        <br />
        {/* <div>{info.EntryBarText ?? 'Click Here'}</div> */}

        <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(info.EntryBarText ?? 'Click Here') }} />
        {info.CallToActionText && (
          <div>
            <br />
            <button
              className="btn btn-info"
              onClick={() => {
                setSelectedBranch(info);
                setShowBranch(true);
              }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(info.CallToActionText) }}></button>
          </div>
        )}
      </div>
    );
  };

  const label = (key: string, fallback: string) => {
    const value = props.state.Settings?.text?.[key];
    if (typeof value !== 'string') return fallback;
    const trimmed = value.trim();
    return trimmed || fallback;
  };

  // Plain text keeps the standard icon. Any HTML is used as saved, so a fair can change or remove the icon.
  const headingHtml = (key: string, fallbackHtml: string, plainIconClass: string) => {
    const raw = label(key, fallbackHtml);
    if (raw.includes('<')) return sanitizeHtml(raw);
    return sanitizeHtml(`<i class="${plainIconClass}"></i>&nbsp;${raw}`);
  };

  const buildScheduleWidget = () => {
    if (dateWidgetData?.data) {
      return (
        <div className="widget">
          <h3
            dangerouslySetInnerHTML={{
              __html: headingHtml('WidgetSchedule', '<i class="fal fa-calendar"></i>&nbsp;Schedule', 'fal fa-calendar'),
            }}
          />
          {dateGroups.map((g) => {
            let groupData = dateWidgetData.data.filter((x) => (x.Group || '') === g);
            return (
              <div key={g} className="schedule-widget-list">
                {g && <h6>{g}</h6>}
                <ul>
                  {groupData.map((x) => {
                    const startDay = FormatDate(x.Date, 'date', true);
                    const endDay = x.EndDate ? FormatDate(x.EndDate, 'date', true) : undefined;
                    const startHasTime = !!x.Date && x.DateHasTime !== false;
                    const endHasTime = !!x.EndDate && x.EndDateHasTime !== false;
                    const startLabel = FormatDate(x.Date, 'pretty', true, startHasTime);
                    const endLabel = FormatDate(x.EndDate, 'pretty', true, endHasTime);
                    const endTime = endHasTime ? FormatDate(x.EndDate, 'time', true) : '';
                    const sub = x.SubLabel ? <span> ({x.SubLabel})</span> : null;
                    const sameDay = !!(startDay && endDay && startDay === endDay);
                    const spansDays = !!(x.EndDate && startDay && endDay && startDay !== endDay);

                    return (
                      <li key={x.PublicId} className={x.IsActive ? 'active' : ''}>
                        {g !== x.Name && <h6>{x.Name}</h6>}
                        {sameDay && (
                          <div>
                            {endTime ? <>{startLabel} to {endTime}</> : startLabel}
                            {sub}
                          </div>
                        )}
                        {spansDays && (
                          <div className="xtext-center">
                            {startLabel} to
                            <div className="xtext-right">{endLabel}</div>
                            {sub}
                          </div>
                        )}
                        {!sameDay && !spansDays && (
                          <div>
                            {startLabel}
                            {sub}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      );
    } else return <></>;
  };

  const buildLinksWidget = () => {
    if (linkWidgetData?.data) {
      return (
        <div className="widget">
          <h3
            dangerouslySetInnerHTML={{
              __html: headingHtml('WidgetImportantLinks', '<i class="fa fa-link"></i>&nbsp;Important Links', 'fa fa-link'),
            }}
          />
          <ul className="important-links">
            {linkWidgetData.data.map((x) => {
              let link = x.ExternalLink ?? x.FlexLink ?? x.SystemLink;
              if(!link && x.PageLink) link = StoreX.BuildUrlWithFair(`../app/page/${x.PageLink}`);
              if (link && link.indexOf('{userId}') > -1) {
                const userId = props.state.User?.id;
                if (!userId) return null;
                link = link.split('{userId}').join(userId);
              }
              return (
                <li key={x.PublicId}>
                  <a href={link} target={x.OpenInNewTab ? '_blank' : '_self'}>
                    {x.Icon && <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(x.Icon) }} />} {x.Name}
                  </a>
                </li>
              );
            })}
            {linkWidgetData.data.length === 0 && <li>{label('WidgetLinksEmpty', 'No links found to display.')}</li>}
          </ul>
        </div>
      );
    }
    return <></>;
  };

  const buildLocationWidget = () => {
    let data = locationWidgetData?.data;
    return (
      <div className="widget">
        <h3
          dangerouslySetInnerHTML={{
            __html: headingHtml('WidgetLocations', '<i class="fa fa-map"></i>&nbsp;Locations', 'fa fa-map'),
          }}
        />
        {data && <>
          <div>
            {data.map((x,i) => {
              return (<div key={i} className="location-entry">
                <h4>{x.Name}</h4>
                {x.LocationDetails && <div dangerouslySetInnerHTML={{__html:sanitizeHtml(x.LocationDetails)}}/>}
                <div>{x.AddressLine1}</div>
                <div>{x.AddressLine2}</div>
                <div>{x.AddressLine3}</div>
                <div>{x.AddressLine4}</div>
                {x.LinkToMap && x.LinkToMap.length > 0 && <div>
                  <a href={x.LinkToMap} target="_blank">{label('WidgetLocationsMap', 'View on Map')}</a>
                </div>}
              </div>);
            })}
            {data?.length === 0 && <div>{label('WidgetLocationsEmpty', 'No location data found to display.')}</div>}
          </div>
        </>}
      </div>
    );
  };

  return (
    <>
      <div className="widget-container">{buildWidget()}</div>
      {showBranch && selectedBranch && (
        <Modal setModalOpen={setShowBranch} title={selectedBranch.Title} size="xl">
          {/* <SetupWizard wizardKey={data.ContentKey ?? ''} /> */}
          <BranchPortal type={selectedBranch.Type} />
        </Modal>
      )}
    </>
  );
};

export default Widget;
